import dayjs, { Dayjs } from "dayjs";
import {
  EventCreate,
  MassTimeSeries,
  ScaleReadingLookup,
} from "src/store/api/generatedApi";

export const AREA_COLORS = ["#2196F3", "#4CAF50", "#FF9800"] as const;

export const getAreaColor = (index: number): string => {
  return AREA_COLORS[index % AREA_COLORS.length] ?? "#888";
};

export const formatHotkey = (hotkeyCode: string): string => {
  // Convert "KeyV" to "V", "KeyB" to "B", etc.
  if (hotkeyCode.startsWith("Key")) {
    return hotkeyCode.slice(3);
  }
  // Convert "Digit1" to "1"
  if (hotkeyCode.startsWith("Digit")) {
    return hotkeyCode.slice(5);
  }
  // Convert "Numpad1" to "1"
  if (hotkeyCode.startsWith("Numpad")) {
    return hotkeyCode.slice(6);
  }
  return hotkeyCode;
};

export const parseFiniteFloat = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const getCandidateLabels = (
  candidate: EventCreate
): { mass: string | null; massValue: number | null; area: string | null } => {
  const massLabelData = candidate.labels.find((l) => l.label_type === "mass");
  const massValue = parseFiniteFloat(massLabelData?.label);
  const mass = massValue !== null ? `~${massValue.toFixed(1)}t` : null;
  const areaLabelData = candidate.labels.find((l) => l.label_type === "area");
  const area = areaLabelData?.label ?? null;
  return { mass, massValue, area };
};

export interface ScaleWeightSeriesData {
  x: Date[];
  y: number[];
}

export const findWeightAtTimestamp = (
  seriesData: ScaleWeightSeriesData | undefined,
  timestamp: Dayjs
): number | null => {
  if (!seriesData || seriesData.x.length === 0) return null;

  const targetMs = timestamp.valueOf();

  const firstTime = seriesData.x[0]?.getTime();
  const lastTime = seriesData.x[seriesData.x.length - 1]?.getTime();

  // If data isn't sorted, fall back to linear scan.
  if (
    firstTime === undefined ||
    lastTime === undefined ||
    firstTime > lastTime
  ) {
    let closestIndex = 0;
    let closestDiff = Infinity;

    for (let i = 0; i < seriesData.x.length; i++) {
      const xVal = seriesData.x[i];
      if (!xVal) continue;
      const diff = Math.abs(xVal.getTime() - targetMs);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    return seriesData.y[closestIndex] ?? null;
  }

  // Binary search for nearest timestamp (assumes x is sorted ascending).
  let left = 0;
  let right = seriesData.x.length - 1;

  if (targetMs <= firstTime) return seriesData.y[0] ?? null;
  if (targetMs >= lastTime) return seriesData.y[right] ?? null;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midTime = seriesData.x[mid]?.getTime() ?? 0;
    if (midTime < targetMs) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  const idx = left;
  const prevIdx = idx > 0 ? idx - 1 : idx;
  const idxTime = seriesData.x[idx]?.getTime() ?? targetMs;
  const prevTime = seriesData.x[prevIdx]?.getTime() ?? targetMs;

  const chosen =
    Math.abs(idxTime - targetMs) < Math.abs(targetMs - prevTime)
      ? idx
      : prevIdx;

  return seriesData.y[chosen] ?? null;
};

const lastIndexAtOrBefore = (
  seriesData: ScaleWeightSeriesData,
  targetMs: number
): number => {
  const firstTime = seriesData.x[0]?.getTime();
  const lastTime = seriesData.x[seriesData.x.length - 1]?.getTime();

  if (
    firstTime === undefined ||
    lastTime === undefined ||
    firstTime > lastTime
  ) {
    return seriesData.x.reduce((best, xVal, index) => {
      if (xVal.getTime() > targetMs) return best;
      const bestTime = seriesData.x[best]?.getTime();
      return bestTime === undefined || xVal.getTime() > bestTime ? index : best;
    }, -1);
  }

  if (targetMs < firstTime) return -1;
  if (targetMs >= lastTime) return seriesData.x.length - 1;

  let left = 0;
  let right = seriesData.x.length - 1;
  while (left < right) {
    const mid = Math.ceil((left + right) / 2);
    if ((seriesData.x[mid]?.getTime() ?? Infinity) <= targetMs) {
      left = mid;
    } else {
      right = mid - 1;
    }
  }
  return left;
};

export const findWeightAtOrBefore = (
  seriesData: ScaleWeightSeriesData | undefined,
  timestamp: Dayjs
): number | null => {
  if (!seriesData || seriesData.x.length === 0) return null;
  const index = lastIndexAtOrBefore(seriesData, timestamp.valueOf());
  return index === -1 ? null : (seriesData.y[index] ?? null);
};

export const findTimeAtOrBefore = (
  seriesData: ScaleWeightSeriesData | undefined,
  timestamp: Dayjs
): Dayjs | null => {
  if (!seriesData || seriesData.x.length === 0) return null;
  const index = lastIndexAtOrBefore(seriesData, timestamp.valueOf());
  const reading = index === -1 ? undefined : seriesData.x[index];
  return reading ? dayjs(reading) : null;
};

export const findTimeAtOrAfter = (
  seriesData: ScaleWeightSeriesData | undefined,
  timestamp: Dayjs
): Dayjs | null => {
  if (!seriesData || seriesData.x.length === 0) return null;
  const targetMs = timestamp.valueOf();
  const atOrBefore = lastIndexAtOrBefore(seriesData, targetMs);
  const candidate = seriesData.x[atOrBefore];
  const index = candidate?.getTime() === targetMs ? atOrBefore : atOrBefore + 1;
  const reading = seriesData.x[index];
  return reading ? dayjs(reading) : null;
};

export const readsAtOrBefore = (lookup: ScaleReadingLookup): boolean =>
  lookup === "at_or_before";

export const findWeightAt = (
  seriesData: ScaleWeightSeriesData | undefined,
  timestamp: Dayjs,
  lookup: ScaleReadingLookup
): number | null =>
  readsAtOrBefore(lookup)
    ? (findWeightAtOrBefore(seriesData, timestamp) ?? seriesData?.y[0] ?? null)
    : findWeightAtTimestamp(seriesData, timestamp);

export const computeVisibleYRange = ({
  seriesData,
  visibleStart,
  visibleEnd,
  nearestCandidate,
  lookup,
}: {
  seriesData: ScaleWeightSeriesData | undefined;
  visibleStart: Dayjs;
  visibleEnd: Dayjs;
  nearestCandidate: EventCreate | null;
  lookup: ScaleReadingLookup;
}): [number, number] | undefined => {
  if (!seriesData) return undefined;

  const visibleStartMs = visibleStart.valueOf();
  const visibleEndMs = visibleEnd.valueOf();

  let minY = Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < seriesData.x.length; i++) {
    const xVal = seriesData.x[i];
    const yVal = seriesData.y[i];
    if (xVal === undefined || yVal === undefined) continue;

    const timestamp = xVal.getTime();
    if (timestamp >= visibleStartMs && timestamp <= visibleEndMs) {
      if (yVal < minY) minY = yVal;
      if (yVal > maxY) maxY = yVal;
    }
  }

  if (minY === Infinity || maxY === -Infinity) {
    for (const yVal of seriesData.y) {
      if (yVal < minY) minY = yVal;
      if (yVal > maxY) maxY = yVal;
    }
    if (minY === Infinity || maxY === -Infinity) {
      return undefined;
    }
  }

  // If there's a nearest candidate, clip the range based on drop weight
  if (nearestCandidate) {
    const { massValue } = getCandidateLabels(nearestCandidate);
    const startTimestamp = dayjs(nearestCandidate.event.start);
    const weightAtStart = findWeightAt(seriesData, startTimestamp, lookup);

    if (massValue !== null && weightAtStart !== null) {
      const boundedDropWeight = Math.max(massValue, 2);
      const centrePoint = weightAtStart + massValue / 2;

      const focused: [number, number] = [
        Math.max(minY, centrePoint - (3 * boundedDropWeight) / 2),
        Math.min(maxY, centrePoint + (3 * boundedDropWeight) / 2),
      ];
      const framed: [number, number] = readsAtOrBefore(lookup)
        ? [
            Math.min(focused[0], weightAtStart),
            Math.max(focused[1], weightAtStart + massValue),
          ]
        : focused;

      if (
        Number.isFinite(framed[0]) &&
        Number.isFinite(framed[1]) &&
        framed[1] > framed[0]
      ) {
        return framed;
      }
    }
  }

  // Fallback: default padding
  const range = maxY - minY;
  const padding = Math.max(1, range * 0.1);

  return [minY - padding, maxY + padding];
};

export const computeLinearDtick = (
  visibleYRange: [number, number] | undefined
): number | undefined => {
  if (!visibleYRange) return undefined;
  const rangeSpan = visibleYRange[1] - visibleYRange[0];
  if (!Number.isFinite(rangeSpan) || rangeSpan <= 0) return undefined;

  // Target ~2-3 ticks, round to a nice number
  const rawStep = rangeSpan / 2;
  if (!Number.isFinite(rawStep) || rawStep <= 0) return undefined;

  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const niceStep =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

  return niceStep * magnitude;
};

export type GroupedScaleWeightSeries = Record<string, ScaleWeightSeriesData>;

export const groupScaleWeightSeriesByArea = (
  series: MassTimeSeries["series"]
): { grouped: GroupedScaleWeightSeries; orderedAreas: string[] } => {
  const grouped: GroupedScaleWeightSeries = {};
  const orderedAreas: string[] = [];
  const seen = new Set<string>();

  for (const point of series) {
    let group = grouped[point.area];
    if (!group) {
      group = { x: [], y: [] };
      grouped[point.area] = group;
    }
    group.x.push(new Date(point.timestamp));
    group.y.push(point.value);

    if (!seen.has(point.area)) {
      seen.add(point.area);
      orderedAreas.push(point.area);
    }
  }

  return { grouped, orderedAreas };
};

export const getAreasToRender = ({
  areasWithScaleWeights,
  dropAreas,
}: {
  areasWithScaleWeights: string[];
  dropAreas: string[];
}): string[] => {
  const areaSet = new Set(areasWithScaleWeights);
  const ordered: string[] = [];
  const added = new Set<string>();

  for (const area of dropAreas) {
    if (areaSet.has(area) && !added.has(area)) {
      ordered.push(area);
      added.add(area);
    }
  }

  for (const area of areasWithScaleWeights) {
    if (!added.has(area)) {
      ordered.push(area);
      added.add(area);
    }
  }

  return ordered;
};

export const resolveTargetArea = ({
  area,
  orderedAreas,
  dropAreas,
}: {
  area: string | null;
  orderedAreas: string[];
  dropAreas: string[];
}): string | null =>
  area ??
  getAreasToRender({ areasWithScaleWeights: orderedAreas, dropAreas })[0] ??
  null;

export const computeMeasurementBounds = ({
  grouped,
  orderedAreas,
  dropAreas,
  area,
  firstMoment,
  lastMoment,
  maxLookbackSeconds,
  lookup,
}: {
  grouped: GroupedScaleWeightSeries;
  orderedAreas: string[];
  dropAreas: string[];
  area: string | null;
  firstMoment: Dayjs;
  lastMoment: Dayjs;
  maxLookbackSeconds: number;
  lookup: ScaleReadingLookup;
}): { measurementStart: Dayjs | null; measurementEnd: Dayjs | null } => {
  if (!readsAtOrBefore(lookup)) {
    return { measurementStart: null, measurementEnd: null };
  }

  const targetArea = resolveTargetArea({ area, orderedAreas, dropAreas });
  const scaleWeights = targetArea ? grouped[targetArea] : undefined;

  const earliest = findTimeAtOrBefore(scaleWeights, firstMoment);
  const latest = findTimeAtOrAfter(scaleWeights, lastMoment);
  const floor = firstMoment.subtract(maxLookbackSeconds, "second");
  const ceiling = lastMoment.add(maxLookbackSeconds, "second");

  return {
    measurementStart: earliest?.isBefore(floor) ? null : earliest,
    measurementEnd: latest?.isAfter(ceiling) ? null : latest,
  };
};
